/**
 * GlassControls — Liquid glass minimize/close/mute control bar.
 *
 * Fixed bottom-center, sits below the avatar during active conversation.
 * Spring-animated entrance. Glassmorphic icon buttons.
 */

import { createGlassPanel, createGlassButton } from '../glass'
import { springEnter, springExit, type SpringAnimation } from '../spring'
import { injectTokens, tokens } from '../tokens'
import { autoDetectAndApply } from '../themeDetector'

export class GlassControls {
  private el: HTMLDivElement | null = null
  private currentAnim: SpringAnimation | null = null
  private minimized = false

  onMinimize: (() => void) | null = null
  onRestore: (() => void) | null = null
  onClose: (() => void) | null = null

  show(): void {
    injectTokens()
    this.hide()

    this.el = createGlassPanel({
      radius: 'pill',
      elevation: 'elevated',
      className: 'hs-controls',
    }) as HTMLDivElement

    Object.assign(this.el.style, {
      position: 'fixed',
      bottom: 'var(--hs-space-3)',
      left: '0',
      right: '0',
      margin: '0 auto',
      width: 'fit-content',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--hs-space-1)',
      padding: '4px',
      zIndex: tokens.zControls,
      opacity: '0',
    })

    autoDetectAndApply(this.el)

    // Minimize button
    const minBtn = createGlassButton({
      label: this.minimized ? '\u25B2' : '\u2014', // ▲ or —
      variant: 'ghost',
      size: 'sm',
      onClick: () => {
        if (this.minimized) {
          this.minimized = false
          minBtn.textContent = '\u2014'
          this.onRestore?.()
        } else {
          this.minimized = true
          minBtn.textContent = '\u25B2'
          this.onMinimize?.()
        }
      },
    })
    Object.assign(minBtn.style, {
      minWidth: '32px',
      height: '32px',
      padding: '0',
      fontSize: '14px',
      borderRadius: '50%',
    })
    this.el.appendChild(minBtn)

    // Close button
    const closeBtn = createGlassButton({
      label: '\u2715',
      variant: 'ghost',
      size: 'sm',
      onClick: () => this.onClose?.(),
    })
    Object.assign(closeBtn.style, {
      minWidth: '32px',
      height: '32px',
      padding: '0',
      fontSize: '14px',
      borderRadius: '50%',
    })
    this.el.appendChild(closeBtn)

    document.body.appendChild(this.el)

    this.currentAnim = springEnter(this.el, {
      preset: 'snappy',
      from: { opacity: 0, scale: 0.9, y: 8 },
    })
  }

  hide(): void {
    this.currentAnim?.stop()
    if (!this.el) return

    const el = this.el
    this.el = null
    this.minimized = false

    this.currentAnim = springExit(el, { remove: true, preset: 'smooth' })
  }
}
